import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Allowed conversion event names for tracking
 */
export type ConversionEventName =
  | "consultation_request_neuropathy"
  | "consultation_request_mens_health"
  | "contact_form"
  | "phone_call"
  | "newsletter_signup"
  | "mailchimp_signup";

/**
 * Tracks a conversion event to both Plausible and Google Analytics
 * @param eventName - Name of the conversion event (must be from ConversionEventName type)
 * @param value - Monetary value of the conversion in USD (e.g., 5900 = $5900, 59 = $59)
 * @param additionalProps - Additional properties to send with the event
 * @param enhancedConversionData - Optional customer data for enhanced conversions (email, phone)
 */
export function trackConversion(
  eventName: ConversionEventName,
  value: number,
  additionalProps: Record<string, any> = {},
  enhancedConversionData?: { email?: string; phone_number?: string },
) {
  try {
    // Track to Plausible
    if (typeof window !== "undefined" && (window as any).plausible) {
      (window as any).plausible(eventName, {
        props: {
          value,
          ...additionalProps,
        },
      });
    }

    // Track to Google Analytics 4
    if (typeof window !== "undefined" && (window as any).gtag) {
      const gtagData: any = {
        event_category: "form",
        event_label: eventName,
        value: value,
        currency: "USD",
        ...additionalProps,
      };

      // Add enhanced conversion data if provided
      if (enhancedConversionData) {
        gtagData.user_data = enhancedConversionData;
      }

      // Track to GA4
      (window as any).gtag("event", eventName, gtagData);

      // Track to Google Ads Conversion - you'll need to set up these conversion labels in Google Ads
      const conversionLabel = getConversionLabel(eventName);
      if (conversionLabel) {
        const adsConversionData: any = {
          send_to: `AW-667741527/${conversionLabel}`,
          value: value,
          currency: "USD",
        };

        // Add enhanced conversion data for Google Ads
        if (enhancedConversionData) {
          adsConversionData.user_data = enhancedConversionData;
        }

        (window as any).gtag("event", "conversion", adsConversionData);
      }
    }
  } catch (error) {
    console.warn("Error tracking conversion:", error);
  }
}

/**
 * Maps conversion event names to Google Ads conversion labels
 * You'll need to create these conversion actions in your Google Ads account
 * and replace these placeholder labels with the actual ones from Google Ads
 */
function getConversionLabel(eventName: ConversionEventName): string | null {
  const conversionLabels: Record<ConversionEventName, string> = {
    consultation_request_neuropathy: "1LEICNv5uNQaENfas74C",
    consultation_request_mens_health: "eOdyCJyFudQaENfas74C",
    contact_form: "wWZ2CJ-FudQaENfas74C",
    phone_call: "TLVZCKKFudQaENfas74C",
    newsletter_signup: "REPLACE_WITH_NEWSLETTER_LABEL",
    mailchimp_signup: "REPLACE_WITH_MAILCHIMP_LABEL",
  };

  return conversionLabels[eventName] || null;
}
